<template>
  <v-dialog
    :model-value="modelValue"
    max-width="500"
    :scrim="true"
    class="mention-dialog-overlay"
    @update:model-value="onDialogToggle">
    <v-card class="mention-dialog">
      <template v-if="!submitted">
        <v-card-title class="mention-dialog__title">Mention a teammate</v-card-title>
        <v-card-text class="mention-dialog__body">
          <div class="mention-dialog__context">
            <span class="mention-dialog__context-label">{{ contextLabel }}</span>
            <span class="mention-dialog__key">{{ displayKey }}</span>
          </div>

          <v-autocomplete
            v-model="recipientIds"
            :items="teammateItems"
            item-title="title"
            item-value="value"
            label="Who should take a look?"
            variant="outlined"
            multiple
            chips
            closable-chips
            autofocus
            :loading="teammatesLoading"
            :menu-props="{ contentClass: 'mention-dialog__menu' }"
            no-data-text="No teammates yet — ask your admin to invite your team."
            hide-details="auto"
            class="mention-dialog__recipients">
            <template #chip="{ props: chipProps, item }">
              <v-chip v-bind="chipProps" size="small" class="mention-dialog__chip">
                <template #prepend>
                  <span class="mention-dialog__avatar mention-dialog__avatar--chip" :style="avatarStyle(item.raw)">
                    {{ item.raw.initials }}
                  </span>
                </template>
                {{ item.raw.title }}
              </v-chip>
            </template>
            <template #item="{ props: itemProps, item }">
              <v-list-item v-bind="itemProps" :subtitle="item.raw.email">
                <template #prepend>
                  <span class="mention-dialog__avatar" :style="avatarStyle(item.raw)">{{ item.raw.initials }}</span>
                </template>
              </v-list-item>
            </template>
          </v-autocomplete>

          <v-textarea
            v-model="message"
            label="Message"
            placeholder="What do they need to look at?"
            variant="outlined"
            rows="3"
            counter
            maxlength="500"
            class="mt-4"
            hide-details="auto"
            @keydown.enter="onMessageEnter" />

          <v-alert v-if="errorMsg" type="error" variant="tonal" density="compact" class="mt-4">
            {{ errorMsg }}
          </v-alert>
        </v-card-text>
        <v-card-actions class="mention-dialog__actions">
          <v-spacer />
          <v-btn variant="text" :disabled="loading" @click="onCancel">Cancel</v-btn>
          <v-btn color="primary" :loading="loading" :disabled="!canSend" @click="onSend">Send</v-btn>
        </v-card-actions>
      </template>

      <template v-else>
        <v-card-text class="mention-dialog__success">
          <div class="mention-dialog__success-icon">
            <v-icon size="28">mdi-check</v-icon>
          </div>
          <div class="mention-dialog__success-title">{{ successTitle }}</div>
          <div class="mention-dialog__success-text">They'll see it in their inbox.</div>
        </v-card-text>
      </template>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from "vue";
import mentionsService from "@/services/mentionsService";
import { useInboxStore } from "@/stores";
import { AVATAR_COLORS } from "@/constants/tagColors";

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  target: {
    type: Object,
    required: true,
    validator: (t) => ["prior_auth", "intake_document", "intake_order"].includes(t?.kind),
  },
});

const emit = defineEmits(["update:modelValue", "sent"]);

const inboxStore = useInboxStore();

const recipientIds = ref([]);
const message = ref("");
const loading = ref(false);
const teammatesLoading = ref(false);
const errorMsg = ref("");
const submitted = ref(false);
const successTitle = ref("");
let closeTimer = null;

const CONTEXT_LABELS = {
  prior_auth: "On order",
  intake_document: "On document",
  intake_order: "On intake order",
};

const contextLabel = computed(() => CONTEXT_LABELS[props.target.kind]);
const displayKey = computed(() =>
  props.target.kind === "intake_order" ? `#${props.target.key}` : String(props.target.key)
);

function initialsOf(user) {
  const first = (user.firstName || "").trim();
  const last = (user.lastName || "").trim();
  const initials = `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  return initials || (user.email || "?").charAt(0).toUpperCase();
}

const teammateItems = computed(() =>
  inboxStore.teammates.map((user) => {
    const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email;
    return {
      title: fullName,
      value: user.id,
      email: user.email,
      initials: initialsOf(user),
      color: AVATAR_COLORS[user.id % AVATAR_COLORS.length],
    };
  })
);

function avatarStyle(item) {
  return {
    background: `color-mix(in srgb, ${item.color} 14%, white)`,
    color: item.color,
  };
}

const canSend = computed(() => recipientIds.value.length > 0 && message.value.trim().length > 0);

watch(
  () => props.modelValue,
  async (open) => {
    clearCloseTimer();
    if (!open) return;
    recipientIds.value = [];
    message.value = "";
    errorMsg.value = "";
    submitted.value = false;
    teammatesLoading.value = true;
    try {
      await inboxStore.fetchTeammates();
    } catch {
      errorMsg.value = "Couldn't load your teammates. Please try again.";
    } finally {
      teammatesLoading.value = false;
    }
  }
);

function clearCloseTimer() {
  if (closeTimer) {
    clearTimeout(closeTimer);
    closeTimer = null;
  }
}

onBeforeUnmount(clearCloseTimer);

function onDialogToggle(value) {
  if (!loading.value) emit("update:modelValue", value);
}

function onCancel() {
  emit("update:modelValue", false);
}

function onMessageEnter(event) {
  if (!(event.metaKey || event.ctrlKey)) return;
  event.preventDefault();
  if (canSend.value && !loading.value) onSend();
}

async function onSend() {
  if (!canSend.value) return;
  loading.value = true;
  errorMsg.value = "";
  try {
    const payload = { recipientIds: recipientIds.value, message: message.value.trim() };
    if (props.target.kind === "prior_auth") payload.orderKey = props.target.key;
    if (props.target.kind === "intake_document") payload.intakeDocumentKey = props.target.key;
    if (props.target.kind === "intake_order") payload.intakeOrderId = props.target.key;
    await mentionsService.createMention(payload);

    const first = teammateItems.value.find((t) => t.value === recipientIds.value[0]);
    const others = recipientIds.value.length - 1;
    const who = others > 0 ? `${first?.title} +${others}` : first?.title;
    successTitle.value = `Mentioned ${who} on ${displayKey.value}`;
    submitted.value = true;
    emit("sent");
    clearCloseTimer();
    closeTimer = setTimeout(() => emit("update:modelValue", false), 1400);
  } catch (e) {
    errorMsg.value = e?.message || "Failed to send. Please try again.";
  } finally {
    loading.value = false;
  }
}
</script>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";

.mention-dialog {
  border-radius: 16px !important;
  box-shadow: $lh_shadow_lg !important;
  overflow: hidden;
}

.mention-dialog__title {
  font-size: $lh_font_size_large;
  font-weight: $lh_font_weight_bold;
  padding: 20px 24px 8px;
}

.mention-dialog__body {
  padding: 8px 24px 16px;
}

.mention-dialog__context {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.mention-dialog__context-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: $lh_color_blue_gray;
}

.mention-dialog__key {
  font-family: $lh_font_mono;
  font-size: 13px;
  color: $lh_color_navy_dark;
  background: rgba($lh_color_navy, 0.06);
  border-radius: 4px;
  padding: 1px 6px;
}

.mention-dialog__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin-right: 10px;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  flex-shrink: 0;
}

.mention-dialog__avatar--chip {
  width: 18px;
  height: 18px;
  margin-right: 4px;
  font-size: 8.5px;
}

.mention-dialog__actions {
  padding: 8px 24px 20px;
}

.mention-dialog__success {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 40px 24px 36px;
}

.mention-dialog__success-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: rgba($lh_sidebar_accent, 0.12);
  color: $lh_sidebar_accent;
  margin-bottom: 14px;
}

.mention-dialog__success-title {
  font-size: 16px;
  font-weight: $lh_font_weight_bold;
  color: $lh_color_navy_dark;
  margin-bottom: 4px;
}

.mention-dialog__success-text {
  font-size: 13px;
  color: $lh_color_blue_gray;
}
</style>

<style lang="scss">
.mention-dialog-overlay .v-overlay__scrim {
  backdrop-filter: blur(4px);
}
</style>
